import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BASE_URL, COMMANDBAR_CUSTOMERID } from '@synergy/config';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ReactElement, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { Stack } from '@mui/material';
import { UserRole } from '@synergy/models';
import { hasRole, sentrySetUser } from '@synergy/utils';
import { useCommandbar } from '@synergy/components-next';
import dayjs from 'dayjs';

import { AccessControl } from './components/access-control/access-control';
import { ChangePassword } from './pages/modals/change-password/change-password';
import { ErrorBoundary } from './components/snackbars/error-boundary';
import { Footer } from './components/footer/footer';
import { Header } from './components/header/header';
import { LoadingSpinner } from './components/loading-spinner/loading-spinner';
import { NotFoundHandler } from './components/not-found-handler/not-found-handler';
import { Routing } from './routing';
import { ScrollToTop } from './components/scroll-to-top/scroll-to-top';
import { ScrollToTopIcon } from './components/scroll-to-top/scroll-to-top-icon';
import { SingleInquiry } from './pages/modals/single-inquiry/single-inquiry';
import { SnackbarNotifier } from './components/snackbars/snackbar-notifier';
import { accountSelector, intlSelector, userSelector } from './store/configuration-selectors';
import { fetchApplicationConfig } from './store/configuration-actions';
import { fetchCategories } from './store/categories-actions';
import { fetchShoppingCartMinimal } from './store/shopping-cart-actions';
import { isLoadingSelector } from './store/feedback-selectors';
import { useAppDispatch, useAppSelector } from '../store/store';

export const App = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const appLoading = useAppSelector(isLoadingSelector);
    const accountLoaded = useAppSelector(accountSelector);
    const user = useAppSelector(userSelector);
    const intl = useAppSelector(intlSelector);
    useCommandbar(COMMANDBAR_CUSTOMERID, user?.id);

    useEffect(() => {
        dispatch(fetchApplicationConfig());
        dispatch(fetchCategories());
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            if (hasRole([UserRole.ShopAdmin, UserRole.ShopUser], user)) {
                dispatch(fetchShoppingCartMinimal());
            }

            sentrySetUser({
                id: user.id.toString(),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, user?.id]);

    useEffect(() => {
        if (user && accountLoaded) {
            // Dispatch user/account details for usage in external scripts (loaded with the `head-tags` script)
            window.dispatchEvent(
                new CustomEvent('synergyUserLoaded', {
                    detail: { user, account: accountLoaded, project: 'shop' },
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, accountLoaded?.id]);

    useEffect(() => {
        dayjs.locale(intl.locale);
    }, [intl.locale]);

    return (
        <HelmetProvider>
            <Helmet>
                <html lang={intl.topLevelLocale} />
            </Helmet>
            <LoadingSpinner isVisible={appLoading} />
            {accountLoaded && user && (
                <IntlProvider {...intl}>
                    <LocalizationProvider
                        localeText={intl.pickerLocale.components.MuiLocalizationProvider.defaultProps.localeText}
                        dateAdapter={AdapterDayjs}
                        adapterLocale={intl.topLevelLocale}
                    >
                        <SnackbarProvider maxSnack={3} anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
                            <SnackbarNotifier />
                            <ErrorBoundary>
                                <Router basename={BASE_URL || '/'}>
                                    <ScrollToTop />
                                    <Header />
                                    <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                                        <SingleInquiry />
                                    </AccessControl>
                                    <ChangePassword />
                                    <NotFoundHandler />
                                    <Stack flex={1}>
                                        <Routing />
                                    </Stack>
                                    <ScrollToTopIcon showBelow={150} />
                                    <Footer />
                                </Router>
                            </ErrorBoundary>
                        </SnackbarProvider>
                    </LocalizationProvider>
                </IntlProvider>
            )}
        </HelmetProvider>
    );
};
